/*
Copyright (C) 2026  Tech9IQ
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The pages' half of SwiftIQ session security (2026-11-03, security fix: after sign-out or a
 * session timeout, the browser's Back button showed the signed-in app again). The server is the
 * authority (server/session, with the root cause): it ends idle sessions, refuses every API call
 * of an ended one at the gateway, and never lets a page or an API answer be stored. Here, the
 * same as the Ops Console's public/admin/session-guard.js:
 *
 *  - Back / Forward: a page the browser restores from its back/forward cache is hidden at once
 *    and stays hidden until the server confirms the session; otherwise it goes to sign-in.
 *    Leaving a page the browser keeps for that cache hides it first, so nothing signed-in is
 *    painted on the way back.
 *  - Activity: real keyboard, mouse, touch or wheel input is reported at most once a minute
 *    (POST /api/session/activity). Screens refreshing themselves send nothing, so an open but
 *    unattended page still reaches the idle limit.
 *  - Idle: once the person has been away for the limit the server reports, the page asks the
 *    server; if the session has ended (here or in another tab), it goes to sign-in, which says
 *    "Your SwiftIQ session has ended".
 * It fails closed: if the server can't be asked, a restored page is not shown.
 *
 * (config.ts inactiveSessionTimeout, a timer in this page only, stays 0: the server's limit
 * replaces it, and counts the person's activity in every tab.)
 */

const HIDDEN = "t9-session-check";
const REPORT_EVERY_MS = 60 * 1000;
const CHECK_EVERY_MS = 30 * 1000;

type SessionState = {
  authenticated: boolean;
  idle?: { timeoutMinutes: number; remainingMs: number };
};

/** The sign-in pages (Swift's own, and the one Tech9IQ signs people in on) have no session to guard. */
export const isSignInPage = (pathname: string = window.location.pathname) =>
  /^\/login(?:\/|$)/.test(pathname);

/** Sign-in, coming back to this page afterwards. The server says whether the session ended. */
export function signInUrl(
  loc: Pick<Location, "pathname" | "search"> = window.location,
): string {
  return `/login?prev=${encodeURIComponent(`${loc.pathname}${loc.search}`)}`;
}

/** What this browser keeps of SwiftIQ's answers (Cacher, ApiLogger): dropped once the session is over. */
export function forgetLocalData(): void {
  try {
    window.localStorage.removeItem("api-cacher");
    window.localStorage.removeItem("apiLog");
  } catch {
    /* storage switched off: nothing kept */
  }
}

let started = false;

/** `navigate`: how the page leaves for sign-in (tests pass their own). */
export function startSessionGuard(
  navigate: (url: string) => void = url => window.location.replace(url),
): void {
  if (started || typeof window === "undefined") return;
  started = true;
  const root = document.documentElement;
  let idleMs = 30 * 60 * 1000; // until the server says otherwise
  let lastInput = Date.now(); // opening the page is the person doing something
  let lastReport = 0;
  let leaving = false;

  // Hidden means hidden: no signed-in content is painted while the session is being checked.
  const style = document.createElement("style");
  style.textContent = `html.${HIDDEN} body{visibility:hidden!important}`;
  (document.head || root).appendChild(style);

  const toSignIn = () => {
    if (leaving) return;
    leaving = true;
    root.classList.add(HIDDEN);
    forgetLocalData();
    // replace(): the signed-in page doesn't stay behind as a Back target.
    navigate(signInUrl());
  };

  const askServer = (): Promise<SessionState> =>
    fetch("/api/session", { cache: "no-store", credentials: "same-origin" })
      .then(r => (r.ok ? r.json() : { authenticated: false }))
      .catch(() => ({ authenticated: false }));

  const learn = (state: SessionState | null) => {
    if (state && state.idle && state.idle.timeoutMinutes > 0)
      idleMs = state.idle.timeoutMinutes * 60 * 1000;
  };

  const report = () => {
    lastReport = Date.now();
    return fetch("/api/session/activity", {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      keepalive: true,
    })
      .then(r => {
        if (r.status === 401) {
          toSignIn();
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then(learn)
      .catch(() => {
        /* a lost report only means the server times out a little sooner */
      });
  };

  const input = () => {
    if (isSignInPage()) return;
    lastInput = Date.now();
    if (lastInput - lastReport >= REPORT_EVERY_MS) void report();
  };
  ["pointerdown", "keydown", "wheel", "touchstart"].forEach(type => {
    window.addEventListener(type, input, { capture: true, passive: true });
  });
  let lastMove = 0;
  window.addEventListener(
    "mousemove",
    () => {
      const t = Date.now();
      if (t - lastMove < 5000) return;
      lastMove = t;
      input();
    },
    { capture: true, passive: true },
  );

  // Back / Forward.
  window.addEventListener("pagehide", event => {
    if (event.persisted && !isSignInPage()) root.classList.add(HIDDEN);
  });
  window.addEventListener("pageshow", event => {
    if (!event.persisted || isSignInPage()) {
      root.classList.remove(HIDDEN);
      return;
    }
    root.classList.add(HIDDEN);
    void askServer().then(state => {
      if (!state.authenticated) return toSignIn();
      learn(state);
      root.classList.remove(HIDDEN);
      return input(); // coming back to the page is activity
    });
  });

  // Idle: ask the server once the person has been away for the limit, and on coming back to the tab.
  const checkIdle = () => {
    if (leaving || isSignInPage() || Date.now() - lastInput < idleMs) return;
    void askServer().then(state => {
      if (!state.authenticated) return toSignIn();
      return learn(state); // still signed in (another tab is in use)
    });
  };
  window.setInterval(checkIdle, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkIdle();
  });

  // Opening a signed-in page: report it, and learn the limit.
  if (!isSignInPage()) void report();
}
